

















































import Vue from 'vue';
import { applicationRepository, fileRepository } from '@/api';
import { configurationServices } from '@/services/configurationService';
import { authService } from '@/services/authService';
import { SpinnerState } from '@/enums/spinnerState';
import { rules } from '@/services/form/rules';
import {
    parseApplicationImport,
    prepareApplicationImport,
} from '@/services/form/applicationImport';
import { createDebugPdf } from '@/services/form/debugPdf';
import { ApplicationForm } from '@/types/forms/specific/applicationForm';
import { UploadField } from '@/types/forms/fields/uploadField';
import { FileUploadConfiguration } from '@/api/interfaces/configuration/form/base/fileUploadConfiguration';
import { ServiceError } from '@/api/interfaces/serviceError';
import { isServiceError } from '@/api/interfaces/typeGuards';
import SpinnerArea from '@/components/molecules/spinnerArea/SpinnerArea.vue';
import AppButton from '@/components/atoms/appButton/AppButton.vue';

export default Vue.extend({
    name: 'ToolImportApplications',
    components: {
        SpinnerArea,
        AppButton,
    },
    data: () => ({
        spinner: SpinnerState.off,
        importText: '',
        anonymize: true,
        addDebugFiles: true,
        parseError: '',
        containsFiles: false,
        resultMessage: '',
    }),
    methods: {
        getErrorMessage(error: unknown): string {
            if (isServiceError(error as ServiceError)) {
                const serviceError = error as ServiceError;
                return serviceError.message
                    ? `${serviceError.messageCode}: ${serviceError.message}`
                    : serviceError.messageCode;
            }

            return error instanceof Error ? error.message : String(error);
        },
        updateImportState(): void {
            const parsed = parseApplicationImport(this.importText);
            this.parseError = parsed.error || '';
            this.containsFiles = parsed.applications.some(
                (application) =>
                    prepareApplicationImport(
                        application,
                        false,
                        authService.email ?? '',
                    ).containsFiles,
            );
            this.resultMessage = '';
        },
        async addDebugPdfs(model: ApplicationForm): Promise<void> {
            const configuration = await configurationServices.form.requestConfigurationOnce();
            const fields =
                configuration.applicationFormConfiguration
                    .documentsConfiguration.fields;
            const documents = (model.documents as unknown) as Record<
                string,
                unknown
            >;

            for (const fieldName in fields) {
                const field = documents[fieldName];
                if (!(field instanceof UploadField) || field.disabled) {
                    continue;
                }

                const uploadConfiguration = fields[
                    fieldName as keyof typeof fields
                ].configurations.find(
                    (configuration) =>
                        configuration.type === 'fileUploadConfiguration',
                ) as FileUploadConfiguration | undefined;
                if (!uploadConfiguration) {
                    continue;
                }

                const response = await fileRepository.uploadFile(
                    createDebugPdf(),
                    uploadConfiguration.scId,
                    'debug-document.pdf',
                    model.applicationId as string,
                );
                if (response.fileInfo) {
                    field.value = response.fileInfo;
                }
            }
        },
        async importApplications(): Promise<void> {
            const parsed = parseApplicationImport(this.importText);
            this.parseError = parsed.error || '';
            this.resultMessage = '';
            if (parsed.error || !parsed.applications.length) {
                return;
            }

            this.spinner = SpinnerState.transparent;
            let importedCount = 0;

            try {
                for (const application of parsed.applications) {
                    const prepared = prepareApplicationImport(
                        application,
                        this.anonymize,
                        authService.email ?? '',
                    );
                    await rules.application.general(prepared.model);
                    prepared.model.lastModified = await applicationRepository.saveApplication(
                        prepared.model,
                    );

                    if (this.addDebugFiles) {
                        await this.addDebugPdfs(prepared.model);
                        await applicationRepository.saveApplication(
                            prepared.model,
                        );
                    }
                    importedCount++;
                }
                this.resultMessage = `${importedCount} Antrag/Anträge importiert.`;
            } catch (error) {
                this.resultMessage = `Import fehlgeschlagen: ${this.getErrorMessage(
                    error,
                )}`;
            } finally {
                this.spinner = SpinnerState.off;
            }
        },
    },
});
